import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector, batch } from 'react-redux';
import Sticky from 'react-sticky-el';
import { useTranslation } from 'react-i18next';

import { UserSessionContext } from 'components/UserSession';
import useURLSearchParams from 'hooks/useURLSearchParams';
import { SearchFieldContext } from 'components/SearchFieldContext';
import {
  searchResultsSelector,
  selectParticipantsFromSearchResults,
  SearchResultsSlice,
} from 'models/searchResults';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import AllSearchResultContainer from 'components/search/AllSearchResultContainer';
import SearchResultContainer from 'components/search/SearchResultContainer';
import ParticipantSearchResultContainer from 'components/search/ParticipantSearchResultContainer';
import EmptyStatePlaceholder from 'components/search/EmptyStatePlaceholder';
import { TabsContext } from 'components/TabsContext';

const RootContainer = styled.div`
  flex: 1 0 auto;
  margin: auto;
  width: 100%;
  max-width: 800px;
`;

const TitleContainer = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 14px;
  color: #A2AAB3;
  padding: 24px 0;
  background-color: #F0F2F5;
`;

const SearchResultLoader = ({ scrollableTarget, viewableTabTypeList }) => {
  const { t } = useTranslation();
  const { activeTabKey } = useContext(TabsContext);
  const query = useURLSearchParams();
  const isDataReady = useSelector((s) => !s.webinars.isLoading);
  const { initialLoading } = useSelector((state) => state.searchResults);

  const {
    exhibitors,
    webinars,
    products,
    speakers,
  } = useSelector(searchResultsSelector);
  const { userSession: { role } } = useContext(UserSessionContext);
  const participants = useSelector(selectParticipantsFromSearchResults(role));

  const {
    searchKeyword,
    setSearchKeyword,
    fetchSearchResults,
  } = useContext(SearchFieldContext);

  const fetchMore = useCallback(() => {
    fetchSearchResults({ newSearchText: searchKeyword });
  }, [fetchSearchResults, searchKeyword]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (isDataReady) {
      batch(() => {
        dispatch(SearchResultsSlice.actions.resetSearchResults());
        fetchMore();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataReady, searchKeyword]);

  useEffect(() => {
    setSearchKeyword(query.get('searchText') || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const tabMap = useMemo(() => (
    {
      all: (<AllSearchResultContainer
        showResult
        exhibitors={exhibitors}
        webinars={webinars}
        participants={participants}
        products={products}
        speakers={speakers}
        fetchMore={fetchMore}
        scrollableTarget={scrollableTarget}
      />),
      exhibitor: (<SearchResultContainer type="exhibitor" results={exhibitors} />),
      webinar: (<SearchResultContainer type="webinar" results={webinars} />),
      participant: (<ParticipantSearchResultContainer participants={participants} fetchMore={fetchMore} scrollableTarget={scrollableTarget} />),
      products: (<SearchResultContainer type="products" results={products} />),
      speakers: (<SearchResultContainer type="speakers" results={speakers} />),
    }
  ), [exhibitors, webinars, participants, fetchMore, scrollableTarget, products, speakers]);
  if (initialLoading) {
    return (
      <RootContainer>
        <TitleContainer>
          {t(
            'search.search_result',
            `Search result of ${searchKeyword}`,
            { query: searchKeyword },
          )}
        </TitleContainer>
        <CircularLoadingIndicator />
      </RootContainer>
    );
  }

  const tablist = viewableTabTypeList.map((type) => tabMap[type]);

  const resultsPresent = (exhibitors && exhibitors.length > 0)
    || (webinars && webinars.length > 0)
    || (participants && participants.length > 0)
    || (products && products.length > 0)
    || (speakers?.length > 0);
  return (
    <RootContainer>
      <Sticky
        scrollElement={scrollableTarget}
        stickyStyle={{ zIndex: 10 }}
      >
        <TitleContainer>
          {t(
            'search.search_result',
            `Search result of ${searchKeyword}`,
            { query: searchKeyword },
          )}
        </TitleContainer>
      </Sticky>
      {
        resultsPresent ? (
          <>
            {tablist.map((Tab, index) => (
              activeTabKey === index && (Tab)))}
          </>
        ) : (
          <EmptyStatePlaceholder />
        )
      }
    </RootContainer>
  );
};

SearchResultLoader.propTypes = {
  scrollableTarget: PropTypes.instanceOf(Element),
  viewableTabTypeList: PropTypes.arrayOf(PropTypes.string).isRequired,
};

SearchResultLoader.defaultProps = {
  scrollableTarget: document.body,
};

export default SearchResultLoader;
